import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Instagram, Linkedin, Youtube, MessageCircle, Facebook, Twitter } from "lucide-react";
import { SITE } from "../../lib/site";
import { useSettings } from "../../context/SettingsContext";

const COLS = [
  {
    title: "Company",
    links: [
      { to: "/about", label: "About Us" },
      { to: "/blog", label: "Blog" },
      { to: "/contact", label: "Contact Us" },
      { to: "/b2b", label: "B2B" },
    ],
  },
  {
    title: "Products",
    links: [
      { to: "/products", label: "All Products" },
      { to: "/products?category=Pickleball Paddles", label: "Pickleball Paddles" },
      { to: "/size-chart", label: "Size Chart" },
      { to: "/moq", label: "MOQ" },
      { to: "/PANTONE.pdf", label: "Pantone Color Guide", external: true },
    ],
  },
  {
    title: "Support & Legal",
    links: [
      { to: "/inquiry", label: "Send Inquiry" },
      { to: "/track", label: "Track Inquiry" },
      { to: "/terms", label: "Terms & Conditions" },
      { to: "/shipping-policy", label: "Shipping Policy" },
      { to: "/privacy-policy", label: "Privacy Policy" },
    ],
  },
];

const Footer = () => {
  const site = useSettings();

  return (
    <footer className="relative overflow-hidden bg-brand-ink text-white" data-testid="footer">
      <div className="blueprint-grid-dark pointer-events-none absolute inset-0 opacity-40" />
      <div className="relative mx-auto max-w-[1400px] px-5 pb-10 pt-16 md:pt-24 lg:px-10">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <div className="flex items-center">
              <img
                src="/pickleball-logo-white-upd.png"
                alt={site.name}
                className="h-20 w-auto object-contain"
              />
            </div>
            <p className="mt-6 max-w-sm text-sm leading-relaxed text-white/50">
              {site.footerAbout}
            </p>
            <div className="mt-8 flex gap-2">
              {[
                { Icon: Instagram, href: site.social_links?.instagram },
                { Icon: Facebook, href: site.social_links?.facebook },
                { Icon: MessageCircle, href: site.social_links?.whatsapp || site.whatsappLink },
                { Icon: Linkedin, href: site.social_links?.linkedin },
                { Icon: Youtube, href: site.social_links?.youtube },
                { Icon: Twitter, href: site.social_links?.twitter },
              ]
                .filter(({ href }) => !!href)
                .map(({ Icon, href }, i) => (
                  <a
                    key={i}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Social link"
                    data-testid={`footer-social-${i}`}
                    className="flex h-10 w-10 items-center justify-center border border-white/20 text-white/70 transition-colors hover:border-brand-red hover:bg-brand-red hover:text-white"
                  >
                    <Icon size={16} />
                  </a>
                ))}
            </div>
          </div>
          {COLS.map((col) => (
            <div key={col.title}>
              <h4 className="mono-caption text-white/40">{col.title}</h4>
              <ul className="mt-5 space-y-3">
                {col.links.map((l) => (
                  <li key={l.label}>
                    {l.external || l.to.endsWith(".pdf") ? (
                      <a
                        href={l.to}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className="text-sm text-white/70 transition-colors hover:text-brand-red inline-flex items-center gap-1"
                      >
                        {l.label} ↗
                      </a>
                    ) : (
                      <Link
                        to={l.to}
                        data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className="text-sm text-white/70 transition-colors hover:text-brand-red"
                      >
                        {l.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-14 border-t border-white/10 pt-8">
          <h4 className="mono-caption text-white/40">Direct Factory Contact</h4>
          <div className="mt-5 grid gap-4 text-sm text-white/70 sm:grid-cols-2 lg:grid-cols-5">
            <a href={site.phoneLink} className="flex items-center gap-3 hover:text-brand-red" data-testid="footer-phone">
              <Phone size={15} className="text-brand-red shrink-0" /> {site.phone}
            </a>
            <a href={site.whatsappLink} target="_blank" rel="noreferrer" className="flex items-center gap-3 hover:text-brand-red" data-testid="footer-whatsapp">
              <MessageCircle size={15} className="text-brand-red shrink-0" /> WhatsApp Direct
            </a>
            <a href={`mailto:${site.email}`} className="flex items-center gap-3 hover:text-brand-red" data-testid="footer-email">
              <Mail size={15} className="text-brand-red shrink-0" /> {site.email}
            </a>
            {site.secondaryEmail && (
              <a href={`mailto:${site.secondaryEmail}`} className="flex items-center gap-3 hover:text-brand-red" data-testid="footer-secondary-email">
                <Mail size={15} className="text-brand-red shrink-0" /> {site.secondaryEmail}
              </a>
            )}
            <span className="flex items-start gap-3" data-testid="footer-address">
              <MapPin size={15} className="mt-0.5 shrink-0 text-brand-red" /> {site.address}
            </span>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-8 sm:flex-row sm:items-center">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/40" data-testid="footer-copyright">
            {site.copyright}. All rights reserved.
          </p>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/40">
            Engineered for the court<span className="text-brand-red">.</span>
          </p>
        </div>
      </div>
      <div className="pointer-events-none relative select-none overflow-hidden">
        <p className="text-outline-white whitespace-nowrap text-center font-display text-[18vw] font-semibold leading-[0.8] tracking-tight">
          PICKLEBALLGEARZ
        </p>
      </div>
    </footer>
  );
};

export default Footer;
